import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { ArrowRight, Check, Clock, DollarSign } from 'lucide-react';
import { useCart } from '../context/CartContext';
import NavBar from '../components/NavBar';
import SmartImage from '../components/SmartImage';

const ServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('services');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadService();
  }, [id]);

  useEffect(() => {
    if (currentPage === 'home') {
      navigate('/');
    } else if (currentPage === 'profile') {
      navigate('/profile');
    } else if (currentPage === 'maintenance') {
      navigate('/maintenance');
    } else if (currentPage === 'accessories') {
      navigate('/accessories');
    } else if (currentPage === 'parts') {
      navigate('/parts');
    } else if (currentPage === 'modifications') {
      navigate('/modifications');
    }
  }, [currentPage, navigate]);

  const loadService = async () => {
    setLoading(true);
    try {
      const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, id));
      if (serviceDoc.exists()) {
        setService({ id: serviceDoc.id, ...serviceDoc.data() });
      }
    } catch (error) {
      console.error('Error loading service:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NavBar currentPage={currentPage} setCurrentPage={setCurrentPage} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        <div className="pt-24 flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div></div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-gray-100">
      <NavBar currentPage={currentPage} setCurrentPage={setCurrentPage} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"><ArrowRight className="w-5 h-5" />العودة</button>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-12 text-white text-center">
              <div className="flex justify-center mb-6">
                <SmartImage
                  src={service.icon}
                  alt={service.name}
                  size="xlarge"
                  fallback="🔧"
                  className="drop-shadow-2xl"
                />
              </div>
              <h1 className="text-5xl font-bold mb-4">{service.name}</h1>
              <div className="flex items-center justify-center gap-8 text-lg">
                <div className="flex items-center gap-2"><DollarSign className="w-6 h-6" /><span className="font-bold">{service.price}</span></div>
                {service.duration && (<div className="flex items-center gap-2"><Clock className="w-6 h-6" /><span>{service.duration}</span></div>)}
              </div>
            </div>
            <div className="p-8 space-y-6">
              {service.description && (<div><h3 className="font-bold text-2xl text-gray-900 mb-3">الوصف</h3><p className="text-gray-700 leading-relaxed text-lg">{service.description}</p></div>)}
              {service.includes && service.includes.length > 0 && (
                <div className="border-t pt-6"><h3 className="font-bold text-2xl text-gray-900 mb-4">ما تشمله الخدمة</h3><ul className="grid md:grid-cols-2 gap-3">
                  {service.includes.map((item, idx) => (<li key={idx} className="flex items-center gap-3 text-gray-700 bg-gray-50 p-3 rounded-lg"><div className="bg-green-100 rounded-full p-1"><Check className="w-5 h-5 text-green-600" /></div>{item}</li>))}
                </ul></div>
              )}
              <div className="border-t pt-6"><button onClick={() => navigate(`/service/${service.id}/book`)} className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg text-lg">احجز الخدمة الآن</button></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetails;
